
import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';
import { ApiException } from '../ClientDTO';
import { ByteArrayStringStringTuple, DocumentDTO, DocumentsListResponse, DocumentTypeDTO } from './DocumentDTO';

export class DocumentClient {
  protected instance: AxiosInstance;
  protected baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(baseUrl?: string, instance?: AxiosInstance) {

      this.instance = instance || axios.create();
      this.instance.interceptors.request.use(
        config => {
            const token = localStorage.getItem('accessToken');
            if (token) {
                if (!config.headers) {
                    config.headers = {};
                }
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            return config;
        },
        error => {
            return Promise.reject(error);
        }
    );
      this.baseUrl = baseUrl ?? "";

  }

  /**
   * @param objectId (optional) 
   * @param documentTypeId (optional) 
   * @param tag (optional) 
   * @return Success
   */
  printdocument(objectId?: number | undefined, documentTypeId?: number | undefined, tag?: string | undefined, cancelToken?: CancelToken): Promise<void> {
      let url_ = this.baseUrl + "/api/document/printdocument?";
      if (objectId === null)
          throw new Error("The parameter 'objectId' cannot be null.");
      else if (objectId !== undefined)
          url_ += "objectId=" + encodeURIComponent("" + objectId) + "&";
      if (documentTypeId === null)
          throw new Error("The parameter 'documentTypeId' cannot be null.");
      else if (documentTypeId !== undefined)
          url_ += "documentTypeId=" + encodeURIComponent("" + documentTypeId) + "&";
      if (tag === null)
          throw new Error("The parameter 'tag' cannot be null.");
      else if (tag !== undefined)
          url_ += "tag=" + encodeURIComponent("" + tag) + "&";
      url_ = url_.replace(/[?&]$/, "");

      let options_: AxiosRequestConfig = {
          method: "GET",
          url: url_,
          headers: {
          },
          cancelToken
      };

      return this.instance.request(options_).catch((_error: any) => {
          if (isAxiosError(_error) && _error.response) {
              return _error.response;
          } else {
              throw _error;
          }
      }).then((_response: AxiosResponse) => {
          return this.processPrintdocument(_response);
      });
  }

  protected processPrintdocument(response: AxiosResponse): Promise<void> {
      const status = response.status;
      let _headers: any = {};
      if (response.headers && typeof response.headers === "object") {
          for (const k in response.headers) {
              if (response.headers.hasOwnProperty(k)) {
                  _headers[k] = response.headers[k];
              }
          }
      }
      if (status === 200) {
          const _responseText = response.data;
          return Promise.resolve<void>(null as any);

      } else if (status !== 200 && status !== 204) {
          const _responseText = response.data;
          return throwException("An unexpected server error occurred.", status, _responseText, _headers);
      }
      return Promise.resolve<void>(null as any);
  }

  uploadDocument(formData: FormData, cancelToken?: CancelToken): Promise<AxiosResponse<DocumentDTO>> {
    const url = `${this.baseUrl}/api/document/uploaddocument`;

    let options_: AxiosRequestConfig = {
        method: "POST",
        url: url,
        data: formData,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        cancelToken
    };

    return this.instance.request<DocumentDTO>(options_).catch((_error: any) => {
        if (this.isAxiosError(_error) && _error.response) {
            return Promise.reject(_error.response);
        } else {
            return Promise.reject(_error);
        }
    });
}

// Utility methods
protected isAxiosError(obj: any): obj is AxiosError {
    return obj && obj.isAxiosError === true;
}


    /**
     * @param documentIdentity (optional) 
     * @return OK
     */
    getdocument(documentIdentity?: string, cancelToken?: CancelToken): Promise<AxiosResponse> {
        let url_ = this.baseUrl + "/api/document/getdocument?";
        if (documentIdentity === null)
            throw new Error("The parameter 'documentIdentity' cannot be null.");
        else if (documentIdentity !== undefined)
            url_ += "documentIdentity=" + encodeURIComponent("" + documentIdentity) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: {
                "Accept": "*/*"
            },
            responseType: 'blob',
            cancelToken
        };

        return this.instance.request(options_).catch((_error: any) => {
            if (axios.isAxiosError(_error) && _error.response) {
                return _error.response;
            } else {
                throw _error;
            }
        });
    }

    protected processGetdocument(response: AxiosResponse): Promise<ByteArrayStringStringTuple> {
        const status = response.status;
        let _headers: any = {};
        if (response.headers && typeof response.headers === "object") {
            for (const k in response.headers) {
                if (response.headers.hasOwnProperty(k)) {
                    _headers[k] = response.headers[k];
                }
            }
        }
        if (status === 200) {
            const _responseText = response.data;
            let result200: any = null;
            let resultData200  = _responseText;
            result200 = ByteArrayStringStringTuple.fromJS(resultData200);
            return Promise.resolve<ByteArrayStringStringTuple>(result200);

        } else if (status !== 200 && status !== 204) {
            const _responseText = response.data;
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
        }
        return Promise.resolve<ByteArrayStringStringTuple>(null as any);
    }

/**
     * @param caseType (optional) 
     * @param caseId (optional) 
     * @return OK
     */
list(caseType?: string | undefined, caseId?: number | undefined, cancelToken?: CancelToken): Promise<DocumentsListResponse[]> {
    let url_ = this.baseUrl + "/api/document/list?";
    if (caseType === null)
        throw new Error("The parameter 'caseType' cannot be null.");
    else if (caseType !== undefined)
        url_ += "caseType=" + encodeURIComponent("" + caseType) + "&";
    if (caseId === null)
        throw new Error("The parameter 'caseId' cannot be null.");
    else if (caseId !== undefined)
        url_ += "caseId=" + encodeURIComponent("" + caseId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
        method: "POST",
        url: url_,
        headers: {
            "Accept": "text/plain"
        },
        cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
            return _error.response;
        } else {
            throw _error;
        }
    }).then((_response: AxiosResponse) => {
        return this.processList(_response);
    });
}

protected processList(response: AxiosResponse): Promise<DocumentsListResponse[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }
    if (status === 200) {
        const _responseText = response.data;
        let result200: any = null;
        let resultData200  = _responseText;
        if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
                result200!.push(DocumentsListResponse.fromJS(item));
        }
        else {
            result200 = <any>null;
        }
        return Promise.resolve<DocumentsListResponse[]>(result200);

    } else if (status !== 200 && status !== 204) {
        const _responseText = response.data;
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DocumentsListResponse[]>(null as any);
}

 /**
     * @param body (optional) 
     * @return OK
     */
 multilist(body?: { [key: string]: number[]; } | undefined, cancelToken?: CancelToken): Promise<DocumentsListResponse[]> {
  let url_ = this.baseUrl + "/api/document/multilist";
  url_ = url_.replace(/[?&]$/, "");

  const content_ = JSON.stringify(body);

  let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
          "Content-Type": "application/json",
      },
      cancelToken
  };

  return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
          return _error.response;
      } else {
          throw _error;
      }
  }).then((_response: AxiosResponse) => {
      return this.processList(_response);
  });
}


  public async getdocumentinfo(documentId?: number, cancelToken?: CancelToken): Promise<DocumentDTO | null> {
    let url_ = this.baseUrl + "/api/document/getdocumentinfo?";
    if (documentId === null) {
      throw new Error("The parameter 'documentId' cannot be null.");
    } else if (documentId !== undefined) {
      url_ += "documentId=" + encodeURIComponent("" + documentId) + "&";
    }
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        Accept: "application/json"
      },
      cancelToken
    };

    try {
      const response = await this.instance.request(options_);
      return this.processGetdocumentinfo(response);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        console.error("Error response:", error.response);
        return null;
      } else {
        console.error("Error:", error);
        throw error;
      }
    }
  }

  protected async processGetdocumentinfo(response: AxiosResponse): Promise<DocumentDTO | null> {
    const status = response.status;
    if (status === 200) {
      const responseData = response.data as DocumentDTO;
      return responseData;
    } else if (status !== 200 && status !== 204) {
      const responseText = response.data;
      console.error("An unexpected server error occurred:", responseText);
      return null;
    }
    return null;
  }

  getavaibledocumentsforaddress(addressId?: number | undefined, cancelToken?: CancelToken): Promise<DocumentTypeDTO[]> {
    let url_ = this.baseUrl + "/api/address/getavaibledocuments?";
    if (addressId === null)
        throw new Error("The parameter 'addressId' cannot be null.");
    else if (addressId !== undefined)
        url_ += "addressId=" + encodeURIComponent("" + addressId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
        method: "GET",
        url: url_,
        headers: {
            "Accept": "text/plain"
        },
        cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
            return _error.response;
        } else {
            throw _error;
        }
    }).then((_response: AxiosResponse) => {
        return this.processGetavaibledocumentsforaddress(_response);
    });
}

protected processGetavaibledocumentsforaddress(response: AxiosResponse): Promise<DocumentTypeDTO[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }
    if (status === 200) {
        const _responseText = response.data;
        let result200: any = null;
        let resultData200  = _responseText;
        if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
                result200!.push(DocumentTypeDTO.fromJS(item));
        }
        else {
            result200 = <any>null;
        }
        return Promise.resolve<DocumentTypeDTO[]>(result200);

    } else if (status !== 200 && status !== 204) {
        const _responseText = response.data;
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DocumentTypeDTO[]>(null as any);
}

getdocumenttypebyteg(tag?: string | undefined, cancelToken?: CancelToken): Promise<DocumentTypeDTO[]> {
    let url_ = this.baseUrl + "/api/form/checkup/getdocumenttypebyteg?";
    if (tag === null)
        throw new Error("The parameter 'tag' cannot be null.");
    else if (tag !== undefined)
        url_ += "tag=" + encodeURIComponent("" + tag) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
        method: "GET",
        url: url_,
        headers: {
            "Accept": "text/plain"
        },
        cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
            return _error.response;
        } else {
            throw _error;
        }
    }).then((_response: AxiosResponse) => {
        return this.processGetdocumenttypebyteg(_response);
    });
}

protected processGetdocumenttypebyteg(response: AxiosResponse): Promise<DocumentTypeDTO[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }
    if (status === 200) {
        const _responseText = response.data;
        let result200: any = null;
        let resultData200  = _responseText;
        if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
                result200!.push(DocumentTypeDTO.fromJS(item));
        }
        else {
            result200 = <any>null;
        }
        return Promise.resolve<DocumentTypeDTO[]>(result200);

    } else if (status !== 200 && status !== 204) {
        const _responseText = response.data;
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DocumentTypeDTO[]>(null as any);
}

   /**
     * @return OK
     */
   getdocumenttypelist( cancelToken?: CancelToken): Promise<DocumentTypeDTO[]> {
    let url_ = this.baseUrl + "/api/document/getdocumenttypelist";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
        method: "POST",
        url: url_,
        headers: {
            "Accept": "text/plain"
        },
        cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
            return _error.response;
        } else {
            throw _error;
        }
    }).then((_response: AxiosResponse) => {
        return this.processGetdocumenttypelist(_response);
    });
}

protected processGetdocumenttypelist(response: AxiosResponse): Promise<DocumentTypeDTO[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }
    if (status === 200) {
        const _responseText = response.data;
        let result200: any = null;
        let resultData200  = _responseText;
        if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
                result200!.push(DocumentTypeDTO.fromJS(item));
        }
        else {
            result200 = <any>null;
        }
        return Promise.resolve<DocumentTypeDTO[]>(result200);

    } else if (status !== 200 && status !== 204) {
        const _responseText = response.data;
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DocumentTypeDTO[]>(null as any);
}

  /**
     * @param casetypeId (optional) 
     * @return OK
     */
  getdocumenttypelistbycasetype(casetypeId?: number | undefined, cancelToken?: CancelToken): Promise<DocumentTypeDTO[]> {
    let url_ = this.baseUrl + "/api/document/getdocumenttypelistbycasetype?";
    if (casetypeId === null)
        throw new Error("The parameter 'casetypeId' cannot be null.");
    else if (casetypeId !== undefined)
        url_ += "casetypeId=" + encodeURIComponent("" + casetypeId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
        method: "POST",
        url: url_,
        headers: {
            "Accept": "text/plain"
        },
        cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
            return _error.response;
        } else {
            throw _error;
        }
    }).then((_response: AxiosResponse) => {
        return this.processGetdocumenttypelistbycasetype(_response);
    });
}

protected processGetdocumenttypelistbycasetype(response: AxiosResponse): Promise<DocumentTypeDTO[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }
    if (status === 200) {
        const _responseText = response.data;
        let result200: any = null;
        let resultData200  = _responseText;
        if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
                result200!.push(DocumentTypeDTO.fromJS(item));
        }
        else {
            result200 = <any>null;
        }
        return Promise.resolve<DocumentTypeDTO[]>(result200);

    } else if (status !== 200 && status !== 204) {
        const _responseText = response.data;
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DocumentTypeDTO[]>(null as any);
}

delete(documentId?: number | undefined, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/document/delete?";
    if (documentId === null)
        throw new Error("The parameter 'documentId' cannot be null.");
    else if (documentId !== undefined)
        url_ += "documentId=" + encodeURIComponent("" + documentId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
        method: "POST",
        url: url_,
        headers: {
        },
        cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
            return _error.response;
        } else {
            throw _error;
        }
    }).then((_response: AxiosResponse) => {
        return this.processDelete(_response);
    });
}

protected processDelete(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }
    if (status === 200) {
        const _responseText = response.data;
        return Promise.resolve<void>(null as any);

    } else if (status !== 200 && status !== 204) {
        const _responseText = response.data;
        return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
}


}




function throwException(message: string, status: number, response: string, headers: { [key: string]: any; }, result?: any): any {
  if (result !== null && result !== undefined)
      throw result;
  else
      throw new ApiException(message, status, response, headers, null);
}

function isAxiosError(obj: any): obj is AxiosError {
  return obj && obj.isAxiosError === true;
}